import { query } from '@confluence/query-preloader-tools';
import { NativeCollabQuery } from '@confluence/native-collab/entry-points/NativeCollabQuery';
import type {
	NativeCollabQueryRes,
	NativeCollabQueryVariables,
} from '@confluence/native-collab/entry-points/NativeCollabQuery';
import { NativeCollabProviderQuery } from '@confluence/fabric-providers/entry-points/NativeCollabProviderQuery';
import type {
	NativeCollabProviderQueryRes,
	NativeCollabProviderQueryVariables,
} from '@confluence/fabric-providers/entry-points/NativeCollabProviderQuery';
import { CollabDraftQuery } from '@confluence/fabric-providers/entry-points/CollabDraftQuery';
import type {
	CollabDraftQueryRes,
	CollabDraftQueryVariables,
} from '@confluence/fabric-providers/entry-points/CollabDraftQuery';
import {
	isDraftSSRPreloadPermissionError,
	markDraftSSRPreloadPermissionError,
} from '@confluence/fabric-providers/entry-points/preloadDraft';
import { fg } from '@confluence/feature-gating';
import { ssrPermissionsErrorHandler } from '@confluence/ssr-utilities';

export function preloadCollabDraftContent(
	contentId: string,
	draftShareId: string | undefined,
	useCache: boolean,
) {
	return query<CollabDraftQueryRes, CollabDraftQueryVariables>({
		query: CollabDraftQuery,
		variables: {
			id: contentId,
			hydrateAdf: true,
			...(draftShareId ? { draftShareId } : undefined),
		},
		fetchPolicy: useCache ? 'cache-first' : 'network-only',
	}).then((result: any) => {
		// There is a bug in appolo client (https://github.com/apollographql/react-apollo/issues/3918)
		// that doesn't preserve the result in caches if there is an error in SSR
		// So we need to preserve the error, to be able to verify it later during render phase in SSR
		if (result?.errors?.some((err) => isDraftSSRPreloadPermissionError(err?.message))) {
			if (fg('confluence_ssr_permissions_error_handler')) {
				ssrPermissionsErrorHandler.markSSRPreloadPermissionError('edit');
			} else {
				markDraftSSRPreloadPermissionError();
			}
		}

		// Preloaded smartlinks don't have contentSlug, while preloaded draft does.
		// Extracting smartlinks from preloaded draft here, so it can be used to fix preloaded smartlinks.
		if (fg('platform_ssr_smartlinks_editor')) {
			const preloadedDraftADF = result?.data?.collabDraft?.document || '';
			const urlMatch = /https?:\/\/[^\s"'<>()]+/g;
			window['__PRELOADED_LINKS_IN_DRAFT__'] = preloadedDraftADF.match(urlMatch) || [];
		}

		return result;
	});
}

export function preloadNativeCollab(
	contentId: string,
	draftShareId: string | undefined,
	useCache: boolean,
	status?: [string],
) {
	return query<NativeCollabQueryRes, NativeCollabQueryVariables>({
		query: NativeCollabQuery,
		variables: {
			contentId,
			...(draftShareId && { draftShareId }),
			status: status ?? ['draft', 'current'],
		},
		fetchPolicy: useCache ? 'cache-first' : 'network-only',
		context: { single: 'true' },
	});
}

export function preloadNativeCollabProvider({
	accountId,
	useCache,
	shouldIncludePermissionType,
}: {
	accountId: string | null;
	useCache: boolean;
	shouldIncludePermissionType: boolean;
}) {
	return query<NativeCollabProviderQueryRes, NativeCollabProviderQueryVariables>({
		query: NativeCollabProviderQuery,
		variables: {
			accountId,
			shouldIncludePermissionType,
		},
		fetchPolicy: useCache ? 'cache-first' : 'network-only',
		context: { single: 'true' },
	});
}
